import { Injectable } from '@angular/core';
import {AccountType, InvalidAction, User, UserData} from '../../../models/user';
import {SegmentService} from 'ngx-segment-analytics';
import * as Sentry from '@sentry/browser';
import {UserService} from '../user.service';
import {ObjectUtils} from '../../utils/object.utils';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import { ActivePage } from '../../../models/event-tracker';
import {appVersion} from '../../../environments/environment';
import { PdfExportService } from '../export-pdf/pdf-export.service';

export enum UserActionType {
  SEARCH = 'Search',
  CREATE = 'Create',
  UPDATE = 'Update',
  LOGIN = 'Login',
  VIEW = 'View',
  ACTION = 'Action',
  SELECTION = 'Selection',
  DELETE = 'Delete',
  TOGGLE = 'Toggle',
  EXPORT = 'Export',
  FETCH = 'Fetch',
  SIGN_UP = 'Sign Up',
  VERIFIED = 'Verified',
  MESSAGE = 'Message',
  SUPPORT = 'Support',
  UPLOAD = 'Upload',
  CHAM_GROUP_CREATE = 'Cham Group Created',
  CHAM_CAMPAIGN_CREATE = 'Cham Created Campaign',
  CHAM_ENABLED_GROUP_DISCOVERY = 'Cham Enabled Group Discovery',
  CHAM_ACCEPT_CAMPAIGN = 'Cham Accepted Campaign Influencer',
  CHAM_INVITED_CAMPAIGN_INFLUENCER = 'Cham Invited Campaign Influencer',
  CHAM_ADD_INFLUENCER_TO_GROUP = 'Cham Added Influencers',
  CHAM_STARTED_TRIAL = 'Cham Started Trial',
  ONBOARDING = 'Onboarding',
  LOAD_DATA_SAMPLES = 'Load Data Samples',
  CHOSE_DATA_SAMPLE = 'Chose Data Sample',
  EMAIL_SETTING = 'Email Settings'
}

export enum ActionDetails {
  ADDED_POSTS = 'Added Posts',
  ADDED_INFLUENCERS = 'Added Influencers',
  REMOVED_INFLUENCERS = 'Removed Influencers',
  MOVED_INFLUENCERS = 'Moved Influencers',
  UPDATED_DETAILS = 'Updated Details',
  ADDED_MENTION = 'Added Mention',
  UPDATE_CONFIG = 'Updated Config',
  INFLUENCER_OPEN_REQUEST = 'Open',
  INFLUENCER_REQUEST_DELETE = 'Closed',
  INFLUENCER_ACCEPTED_REQUEST = 'Accepted',
  INFLUENCER_REJECTED_REQUEST = 'Declined',
  INFLUENCER_CAMPAIGN_ARCHIVED = 'Archived',
  CONFIRMED_DETAILS = 'Confirmed Details',
  INFLUENCER_INVITE_REQUEST = 'Invited',
  INFLUENCER_DECLINED_REQUEST = 'Declined',
  INFLUENCER_CONFIRM_CHANNEL = 'Confirmed Channel',
  REQUEST_UPGRADE = 'Request Upgrade',
  IDENTIFY_RELEVANT_INFLUENCERS_ON = 'On',
  IDENTIFY_RELEVANT_INFLUENCERS_OFF = 'Off',
  UPDATED_DISCOVERY_FILTERS = 'Updated Discovery Filters',
  ADDED_INFLUENCERS_FOR_REVIEW = 'Added Influencers For Review',
  BULK_UPLOAD = 'Bulk Upload',
  ARCHIVED_CAMPAIGN = 'Archived Campaign',
  UNARCHIVED_CAMPAIGN = 'Unarchived Campaign',
  HANDLE_SEARCH = 'Handle Search'
}

export enum Page {
  GROUP = 'Influencer Group',
  CAMPAIGN = 'Campaign',
  HOME = 'Home',
  DEMOGRAPHICS = 'Influencer Demographics',
  MEASURE = 'Measure',
  DISCOVER = 'Discover',
  REPORT = 'Report',
  PROJECT = 'Project',
  MENTION = 'Mention',
  SUB_SCRIPTION = 'Sub Scription',
  HOME_TAB = 'Home Tab',
  DISCOVER_TAB = 'Discover Tab',
  INFLUENCER_TAB = 'Influencer Tab',
  CAMPAIGN_TAB = 'Campaign Tab',
  MEASURE_TAB = 'Measure Tab',
  INBOX_TAB = 'Inbox Tab',
  CHANGE_PASSWORD_MODAL = 'Change Password Modal',
  SETTING_PAGE = 'Setting Page',
  BILLING_PAGE = 'Billing Page',
  NOTIFICATION = 'Notification',
  LOGIN = 'Login',
  INFLUENCER_DETAILS = 'Influencer Details',
  INFLUENCER_OVERVIEW = 'Influencer Overview',
  INFLUENCER_INSIGHTS = 'Influencer Insights',
  INFLUENCER_CAMPAIGNS = 'Influencer Campaigns',
  INFLUENCER = 'Influencer',
  POST = 'Post',
  IG_STORY = 'Instagram Story',
  WALL_NOTIFICATION = 'Wall Notification',
  CHAT = 'Chat',
  CAMPAIGN_INVITATION = 'Campaign Invitation',
  CONTACT_PERMISSION =  'Contact Permission',
  CHANNEL = 'Channel',
  ACCOUNT_DETAILS = 'Account Details',
  CAMPAIGN_DETAILS = 'Campaign Details',
  INFLUENCER_GROUP = 'Influencer Group',
  RECENTLY_VIEWED_INFLUENCERS_GROUP = 'Recently Viewed Influencers',
  PROFILE = 'Profile',
  IMAGE = 'Image',
  SETTINGS_PAGE = 'Account Settings Page',
  CAMPAIGN_SETTING = 'Campaign Settings',
  EMAIL_SETTING = 'Email Settings'
}

export enum ActivePages {
  CAMPAIGN_OVERVIEW = 'Campaign Overview',
  CAMPAIGN_NEW = 'New Campaign',
  CAMPAIGN_EDIT = 'Edit Campaign',
  DISCOVER = 'Discover',
  GROUP_OVERVIEW = 'Group Overview',
  GROUP_DETAILS = 'Group Details',
  INFLUENCER_DETAILS = 'Influencer Details',
  INFLUENCER_OVERVIEW = 'Influencer Overview',
  INFLUENCER_INSIGHTS = 'Influencer Insights',
  INFLUENCER_DEMOGRAPHICS = 'Influencer Demographics',
  INFLUENCER_CAMPAIGNS = 'Influencer Campaigns',
  LOGIN = 'Login',
  MENTION = 'Mention',
  MEASURE = 'Measure',
  INBOX = 'Inbox',
  POST = 'Post Overview',
  CAMPAIGN_HISTORY = 'Campaign History',
  CAMPAIGN_DETAILS = 'Campaign Details',
  ACTIVE_CAMPAIGN = 'Active Campaign',
  ARCHIVED_CAMPAIGN = 'Archived Campaign',
}

export enum TargetType {
  INFLUENCER = 'Influencer',
  ADVERTISER = 'Advertiser',
  CAMPAIGN = 'Campaign',
  CAMPAIGN_DETAILS = 'Campaign Details',
  INFLUENCER_GROUP = 'Influencer Group',
  POST = 'Post',
  PROJECT = 'Project',
  FILTER = 'Filter',
  SORT = 'Sort',
  DATE_RANGE = 'Date Range',
  MODULES = 'Modules',
  REPORT = 'Report',
  BUTTON = 'Button',
  BACKDROP = 'Backdrop',
  OPEN_CONTACT_TAB = 'Open Contact Tab',
  ACCOUNT =  'Account',
  CHANNEL = 'Channel',
  REQUEST_REVIEW = 'Request Review',
  USER = 'User',
  PROFILE_IMAGE = 'Profile Image',
  GROUP_DISCOVERY = 'Group Discovery'
}

// We're adding action_source as a new field to action events, they explain where a user performed a specific action
export enum ActionSource {
  PAGE = 'Page', // Button On Page
  MODAL = 'Modal', // On Modal
  BOTTOM_BAR = 'Bottom Bar', // Unexpanded Bottom Bar
  ACTION_VIEW = 'Action View', // Expanded Bottom Bar
  CAMPAIGN_LANDING_PAGE = 'Landing Page',
  INFLUENCER_APP = 'Influencer App',
  UNDO_LINK = 'Undo Link',
  COMMUNICATION_PAGE = 'Communications Page',
  ACTIVITY_TAB = 'Activity Tab',
  PENDING_LIST = 'Pending List',
  GROUP_SETTINGS = 'Group Settings'
}

@Injectable()
export class EventTrackerService {

  private superProperties: any;
  private hasIdentified = new BehaviorSubject(false);
  private authorizedCompanyId: number;

  constructor(private segment: SegmentService,
              private userService: UserService,
              private pdfExportService: PdfExportService) {
    this.userService.companyId.subscribe(activeCompany => {
      if (activeCompany && activeCompany !== this.authorizedCompanyId) {
        this.identifyCurrentUserWithSegment(activeCompany, this.userService.accountType());
      }
    });
    this.userService.accountLevels.subscribe(accountLevel => {
      if (accountLevel) {
        this.updateCompanySubscription(accountLevel.level.name);
        if (!this.hasIdentified.getValue()) {
          this.identifyCurrentUserWithSegment(this.userService.getCompanyId(), accountLevel.level.name);
        }
      }
    });
    this.preventPdfTracking();
  }

  get isIdentifiedUser(): boolean {
    return this.hasIdentified.getValue();
  }

  get hasIdentifiedObserver(): Observable<boolean> {
    return this.hasIdentified.asObservable();
  }

  private identifyCurrentUserWithSegment(activeCompany, accountLevel) {
    const user = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    if (user) {
      this.identifyUser(user, activeCompany, accountLevel);
    }
  }

  /**
   * This method is used to identify an user when they login to app
   * @param {User} user
   */

  public identifyUser(user: User, currentActiveCompanyId?: number, accountLevel?: string, callback?) {
    const userData: UserData = user.data;

    // Fallback if user is undefined
    if (!userData) {
      Sentry.captureException(new Error('Attempted to identify user without userData object'));
      this.hasIdentified.next(false);
      return;
    }

    this.superProperties = Object.assign({}, this.superProperties, {
      // super properties
      user_id: userData.id,
      user_name: userData.fullName,
      user_company_id: userData.company.id,
      user_company_name: userData.company.name,
      company: {
        id: userData.company.id,    // Intercom
        name: userData.company.name // Intercom
      },
      currently_active_company_name: this.userService.getCompanyName(currentActiveCompanyId),
      currently_active_company_id: currentActiveCompanyId,
      user_role: userData.role.name,
      user_creation_date: new Date(userData.createdAt).toISOString(),
      user_company_subscription: accountLevel,
      user_company_account_type:
        (userData.company.companyType.name.match(/Agency/) ? 'Agency' : userData.company.companyType.name),
      user_company_account_sub_type: userData.company.companyType.name,
      app_version: appVersion,
      has_started_trial: this.userService.getStartedTrialValue,
      is_sso_user: userData.isSsoUser,
      signup_entity: userData.signupEntity,
      signup_source: userData.signupSource
    });

    const params = {
      // people properties
      email: userData.email,
      name: userData.fullName,
      created: userData.createdAt,
      createdAt: userData.createdAt,
      last_login: new Date(),
      ...this.superProperties
    };

    const custom_settings = {
      Intercom: { hideDefaultLauncher: true }
    };

    // register user
    this.segment.identify(userData.id, params, custom_settings).then(() => {
      this.authorizedCompanyId = currentActiveCompanyId;
      this.hasIdentified.next(true);
      if (callback) {
        callback();
      }
    }, (error) => {
      this.hasIdentified.next(false);
      const userId = userData.id;
      const detailsError = error.toString();
      Sentry.captureException(new Error('An error happen when identify user in segment'));
    });

    this.segment.page();
  }

  /**
   * Track user sign-up
   * @param {User} userInfo
   * @param isInvited
   */
  public trackUserSignup(userInfo: User, isInvited) {
    this.identifyUser(userInfo, null, null, () => {
      this.segment.track(UserActionType.SIGN_UP, {
        source: isInvited ? 'Invite' : 'Self-Service'
      }).then((res) => {}, (error) => {
        const detailsError = error.toString();
        Sentry.captureException(new Error(`An error happen while tracking event ${UserActionType.SIGN_UP}`));
      });
    });
  }

  /**
   * Track when user open the email settings page
   * @param userId
   */
  public trackEmailSetting(userId) {
    const custom_settings = {
      Intercom: { hideDefaultLauncher: true }
    };
    this.segment.identify(userId, { name: userId }, custom_settings).then(() => {
      this.hasIdentified.next(true);
      this.segment.track(UserActionType.VIEW, { item: Page.EMAIL_SETTING }).then((res) => {}, (error) => {
        Sentry.captureException(new Error(`An error happen while tracking event ${UserActionType.EMAIL_SETTING}`));
      });
    }, (error) => {
      this.hasIdentified.next(false);
      Sentry.captureException(new Error('An error happen when identify user in segment'));
    });
  }

  /**
   * Track whenever user verify their email successfully
   * @param {User} userInfo
   * @param isInvited
   */
  public trackUserVerifyAccount(userInfo: User) {
    this.identifyUser(userInfo, null, null, () => {
      this.segment.track(UserActionType.VERIFIED).then((res) => {}, (error) => {
        const detailsError = error.toString();
        Sentry.captureException(new Error(`An error happen while tracking event ${UserActionType.VERIFIED}`));
      });
    });
  }

  /**
   * A wrapper of Segment event tracker. Use it to track user behaviour
   * @param {UserActionType} action
   * @param {Page} item
   * @param target
   * @param params
   * @param report
   * @param {ActivePage} activePage
   */
  public trackUserAction(action: UserActionType, item?: Page, target?: any, params?: any, report?: any, activePage?: ActivePage, extraProperties?: any) {
    // Only allow to track user action when user is identified successfully
    if (!this.isIdentifiedUser) {
      return;
    }
    const properties = this.buildSegmentProperties(action, item, target, params, report, activePage, extraProperties);

    this.segment.track(action, properties).then((res) => {}, (error) => {
      const detailsError = error.toString();
      Sentry.captureException(new Error('An error happen when tracking user actions'));
    });
  }

  public trackUserInvalidAction(detailsAction: InvalidAction, action: UserActionType, item: Page,
                                target?: any, params?: any, report?: any, activePage?: ActivePage,
                                extraProperties?: any) {
    // Only allow to track user action when user is identified successfully
    if (!this.isIdentifiedUser) {
      return;
    }
    let properties = this.buildSegmentProperties(action, item, target, params, report, activePage, extraProperties);
    if (detailsAction.blocked) {
      properties = Object.assign({}, properties, {
        blocked: detailsAction.blocked,
        blocked_reason: detailsAction.blockedReason,
        wall_type: detailsAction.wallType,
        wall_name: detailsAction.wallName
      });
    }

    if (detailsAction.limitEnabled) {
      properties = Object.assign({}, properties, {
        limit_enabled: detailsAction.limitEnabled,
        limit_value: detailsAction.limitValue,
        // for some features, we can only do one thing at a time, so desired value = maxValue + 1
        limit_desired: detailsAction.limitDesired ? detailsAction.limitDesired : detailsAction.limitValue + 1
      });
    }
    properties = Object.assign({}, properties, {
      account_level: this.userService.currentUserLevel ? this.userService.currentUserLevel.name : null
    });

    this.segment.track(action, properties).then((res) => {}, (error) => {
      const detailsError = error.toString();
      Sentry.captureException(new Error('An error happen when tracking user actions'));
    });
  }

  public trackUserRequestUpdate() {
    // Only allow to track user action when user is identified successfully
    if (!this.isIdentifiedUser) {
      return;
    }

    const properties = {
      action: ActionDetails.REQUEST_UPGRADE,
      sp: this.superProperties,
    };

    this.segment.track(UserActionType.SUPPORT, properties).then((res) => {}, (error) => {
      const detailsError = error.toString();
      Sentry.captureException(new Error(`An error happen when tracking event Request Upgrade`));
    });
  }

  public updateCompanySubscription(accountLevel) {
    if (this.superProperties) {
      this.superProperties.user_company_subscription = accountLevel;
    }
  }

  private buildSegmentProperties(action: UserActionType, item: Page, target?: any,
                                 params?: any, report?: any, activePage?: ActivePage, extraProperties?: any) {
    let properties = { item, app_version: 2 };
    if (target) {
      properties = Object.assign({}, properties, {
        target_id: target.id,
        target_name: target.name,
        target_type: target.type,
        filter_name: target.filterName,
        filter_id: target.filterId
      });
    }

    if (report) {
      properties = Object.assign({}, properties, {
        report_id: report.id,
        report_name: report.name
      });
    }

    if (action === UserActionType.ACTION) {
      properties = Object.assign({}, properties, {
        action: params.action,
        action_source: params.source,
        action_details: params.actionDetails
      });
      if (params.action_status) {
        properties = Object.assign({}, properties, {
          action_status: params.action_status,
          action_status_details: params.action_status_details
        });
        delete params.action_status;
        delete params.action_status_details;
      }
      if (params.action_stories_count >= 0) {
        properties = Object.assign({}, properties, {
          action_stories_count: params.action_stories_count,
          action_stories_timing: params.action_stories_timing
        });
        delete params.action_stories_count;
        delete params.action_stories_timing;
      }
    }
    // special case handle for campaign invitation
    if (action === UserActionType.VIEW && params && params.actionDetails && params.source) {
      properties = Object.assign({}, properties, {
        action_source: params.source,
        action_details: params.actionDetails
      });
    }

    if (params) {
      delete params.action;
      delete params.source;
      delete params.actionDetails;
      if (Object.keys(params).length !== 0) {
        properties = Object.assign({}, properties, {params: params});
      }
    }

    if (activePage) {
      properties = Object.assign({}, properties, {
        active_page_type: activePage.type,
        active_page_name: activePage.name,
        active_page_id : activePage.id,
      });
    }

    if (extraProperties) {
      properties = Object.assign({}, properties, extraProperties);
    }

    // add super properties
    properties = Object.assign({}, properties, {
      sp: this.superProperties
    });
    return properties;
  }

  private preventPdfTracking() {
    this.segment.addSourceMiddleware(({ integrations, payload, next }) => {
      if (!this.pdfExportService.exportingPdf) {
        next(payload);
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/event-tracker/event-tracker.service.ts